// src/pages/Hr/WorkSchedulesPage.jsx
// =============================================================================
// HR Work Schedules / operational weekly schedule management
// =============================================================================
//
// PURPOSE
// -------
// This page is the operational scheduling workspace for reception and
// superusers.  It deliberately keeps schedule management out of the full user
// settings card so reception can maintain day-to-day rosters without receiving
// unrelated administrative permissions.
//
// Batch 1 scope kept here:
// - week picker,
// - employee selection,
// - employee × day grid,
// - source labels per day,
// - cell dialog for user_work_schedule_overrides,
// - save/delete daily override.
//
// Batch 2 scope added here:
// - preview of the employee's currently effective rotating work-pattern
//   assignment,
// - assigning an existing rotating pattern from the HR module,
// - reviewing assignment history for the selected employee,
// - deactivating the active assignment without deleting history.
//
// IMPORTANT ARCHITECTURAL BOUNDARY
// --------------------------------
// React does NOT reproduce schedule-priority calculations.  The grid asks the
// backend preview endpoint for the effective result for every day.  That
// endpoint uses leaveDurationService, which is also the source used by leave
// request calculation.  This keeps the planning UI and leave accounting aligned.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useTranslation } from "react-i18next";

import { fetchEmployees } from "../../services/employeeService";
import {
  deleteDailyScheduleOverride,
  bulkSaveExactSchedule,
  deleteExactSchedule,
  deletePatternAssignment,
  fetchExactSchedules,
  fetchPatternAssignments,
  fetchWorkScheduleAudit,
  fetchWorkPatterns,
  fetchWorkSchedulePreview,
  saveDailyScheduleOverride,
  saveExactSchedule,
  savePatternAssignment,
} from "../../services/hrWorkScheduleService";

import "./WorkSchedulesPage.css";

/* -------------------------------------------------------------------------- */
/* Date helpers                                                               */
/* -------------------------------------------------------------------------- */

function pad2(value) {
  return String(value).padStart(2, "0");
}

function toDateOnly(dateLike) {
  const d = dateLike instanceof Date ? dateLike : new Date(dateLike);
  if (!Number.isFinite(d.getTime())) return "";
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function parseDateOnly(dateOnly) {
  const [year, month, day] = String(dateOnly || "").split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day, 12, 0, 0, 0);
}

function addDays(date, days) {
  const d = new Date(date.getTime());
  d.setDate(d.getDate() + days);
  return d;
}

function startOfWeekMonday(dateLike) {
  const d = dateLike instanceof Date ? new Date(dateLike.getTime()) : new Date(dateLike);
  if (!Number.isFinite(d.getTime())) return startOfWeekMonday(new Date());
  d.setHours(12, 0, 0, 0);
  const jsDay = d.getDay();
  const diff = jsDay === 0 ? -6 : 1 - jsDay;
  return addDays(d, diff);
}

function buildWeekDays(weekStartDateOnly) {
  const start = parseDateOnly(weekStartDateOnly) || startOfWeekMonday(new Date());
  return Array.from({ length: 7 }, (_, index) => {
    const date = addDays(start, index);
    return {
      date,
      dateOnly: toDateOnly(date),
      jsDay: date.getDay(),
      dayOfMonth: date.getDate(),
      isWeekend: date.getDay() === 0 || date.getDay() === 6,
      isToday: toDateOnly(date) === toDateOnly(new Date()),
    };
  });
}

function formatMinutes(minutes, t) {
  const total = Math.max(0, Math.round(Number(minutes || 0)));
  const h = Math.floor(total / 60);
  const m = total % 60;

  if (total === 0) return t("leaves:workSchedules.freeDay", { defaultValue: "Free" });
  if (m === 0) return `${h}h`;
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

function normalizeSourceRef(sourceRef) {
  const m = /^override:([0-9]+)$/.exec(String(sourceRef || ""));
  return m ? Number(m[1]) : null;
}

function isSourceEditable(source) {
  // Batch 1 edits only daily employee overrides.  Any cell may open the dialog
  // and create an override, but only an existing EMPLOYEE_OVERRIDE has a delete
  // target.  Facility exceptions are still viewable because users should see
  // why the facility is closed, but a daily override cannot override that source.
  return String(source || "").toUpperCase() !== "FACILITY_EXCEPTION";
}

function normalizeSource(source) {
  return String(source || "NON_WORKING_DEFAULT").toUpperCase();
}

function isExceptionOrConflictSource(source) {
  // The schedule preview currently returns one final effective source per day.
  // For the productivity filter, we treat the sources that represent explicit
  // planning interventions or stronger-than-usual rules as operationally
  // interesting.  Plain rotating patterns, profile fallbacks and normal free
  // days stay visible in the normal grid, but they do not qualify a row for
  // the "exceptions/conflicts only" filter.
  const key = normalizeSource(source);
  return key === "FACILITY_EXCEPTION"
    || key === "EMPLOYEE_OVERRIDE"
    || key === "USER_WORK_SCHEDULE";
}

function hasOperationalWarnings(day) {
  return Array.isArray(day?.warnings) && day.warnings.length > 0;
}

function hasAbsenceInfo(day) {
  return Array.isArray(day?.absences) && day.absences.length > 0;
}

function hasExceptionOrConflictDay(day) {
  // Absences are intentionally not included here.  A scheduled working day with
  // an approved leave request is normal business logic: the schedule defines the
  // employee's normal working time and the absence consumes it.  True conflicts
  // remain represented by warnings, for example visits on approved absences.
  return isExceptionOrConflictSource(day?.source) || hasOperationalWarnings(day);
}

function warningSeverityClass(warnings) {
  const list = Array.isArray(warnings) ? warnings : [];
  if (list.some((warning) => String(warning.severity || "").toLowerCase() === "error")) {
    return "error";
  }
  if (list.some((warning) => String(warning.severity || "").toLowerCase() === "warning")) {
    return "warning";
  }
  return "info";
}

function warningLabel(warning, t) {
  const type = String(warning?.type || "UNKNOWN").toUpperCase();
  return t(`leaves:workSchedules.warnings.types.${type}`, {
    defaultValue: warning?.message || type.replace(/_/g, " "),
  });
}

function absenceLabel(absence, t) {
  const type = String(absence?.type || "UNKNOWN").toUpperCase();
  return t(`leaves:workSchedules.absences.types.${type}`, {
    defaultValue: absence?.message || type.replace(/_/g, " "),
  });
}

function sourceLabel(source, t) {
  const key = normalizeSource(source);
  return t(`leaves:workSchedules.sources.${key}`, {
    defaultValue: key.replace(/_/g, " "),
  });
}

function buildDayTooltip({ user, day, value, t }) {
  const source = normalizeSource(value?.source);
  const lines = [
    `${user?.title || `User ${user?.id || ""}`} · ${day.dateOnly}`,
    `${t("leaves:workSchedules.tooltipMinutes", { defaultValue: "Minutes" })}: ${formatMinutes(value?.minutes || 0, t)}`,
    `${t("leaves:workSchedules.tooltipSource", { defaultValue: "Source" })}: ${sourceLabel(source, t)}`,
  ];

  if (value?.source_ref) {
    lines.push(`${t("leaves:workSchedules.tooltipReference", { defaultValue: "Reference" })}: ${value.source_ref}`);
  }
  if (value?.note) {
    lines.push(`${t("leaves:workSchedules.tooltipNote", { defaultValue: "Note" })}: ${value.note}`);
  }

  if (Array.isArray(value?.absences) && value.absences.length > 0) {
    lines.push(t("leaves:workSchedules.tooltipAbsences", { defaultValue: "Absences" }));
    value.absences.forEach((absence) => {
      lines.push(`• ${absenceLabel(absence, t)}`);
    });
  }

  if (Array.isArray(value?.warnings) && value.warnings.length > 0) {
    lines.push(t("leaves:workSchedules.tooltipWarnings", { defaultValue: "Warnings" }));
    value.warnings.forEach((warning) => {
      lines.push(`• ${warningLabel(warning, t)}`);
    });
  }

  return lines.filter(Boolean).join("\n");
}

function formatDateRange(from, to, t) {
  if (!from && !to) return t("leaves:workSchedules.openRange", { defaultValue: "Open-ended" });
  if (from && to) return `${from} – ${to}`;
  if (from) return t("leaves:workSchedules.fromDate", { date: from, defaultValue: `from ${from}` });
  return t("leaves:workSchedules.untilDate", { date: to, defaultValue: `until ${to}` });
}

function normalizePatternDays(days) {
  return (Array.isArray(days) ? days : [])
    .map((day) => ({
      week_index: Number(day.week_index || 0),
      day_of_week: Number(day.day_of_week || 0),
      minutes: Number(day.minutes || 0),
    }))
    .filter((day) => day.week_index > 0 && day.day_of_week > 0)
    .sort((a, b) => (a.week_index - b.week_index) || (a.day_of_week - b.day_of_week));
}


function jsDayToIsoDay(jsDay) {
  return jsDay === 0 ? 7 : jsDay;
}

function buildEmptyWeekDays() {
  return Array.from({ length: 7 }, (_, index) => ({
    day_of_week: index + 1,
    minutes: 0,
  }));
}

function buildDaysFromPreviewRow(row) {
  const byDow = new Map();
  for (const day of row?.days || []) {
    const date = parseDateOnly(day.work_date);
    if (!date) continue;
    byDow.set(jsDayToIsoDay(date.getDay()), Number(day.minutes || 0));
  }
  return buildEmptyWeekDays().map((item) => ({
    ...item,
    minutes: byDow.has(item.day_of_week) ? byDow.get(item.day_of_week) : 0,
  }));
}

function buildDaysFromExactSchedule(schedule) {
  const byDow = new Map();
  for (const day of schedule?.days || []) {
    byDow.set(Number(day.day_of_week), Number(day.minutes || 0));
  }
  return buildEmptyWeekDays().map((item) => ({
    ...item,
    minutes: byDow.has(item.day_of_week) ? byDow.get(item.day_of_week) : 0,
  }));
}

function formatIsoDayLabel(dayOfWeek, t) {
  return t(`leaves:workSchedules.patternDayNames.${dayOfWeek}`, { defaultValue: String(dayOfWeek) });
}

const QUICK_EDIT_PRESETS = [0, 240, 360, 480];

const WORK_SCHEDULE_VIEW_STORAGE_KEY = "hb_visits.hr.workSchedules.view.v1";

function readSavedWorkScheduleView() {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(WORK_SCHEDULE_VIEW_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeSavedWorkScheduleView(value) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(WORK_SCHEDULE_VIEW_STORAGE_KEY, JSON.stringify(value || {}));
  } catch {
    // Local storage is a convenience feature only.  The scheduling page must
    // remain usable even if the browser blocks persistence.
  }
}

function clearSavedWorkScheduleView() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(WORK_SCHEDULE_VIEW_STORAGE_KEY);
  } catch {}
}

function formatDateTime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (!Number.isFinite(d.getTime())) return String(value);
  return d.toLocaleString();
}

function auditEntryLabel(entry, t) {
  const type = String(entry?.type || "UNKNOWN").toUpperCase();
  return t(`leaves:workSchedules.audit.types.${type}`, {
    defaultValue: type.replace(/_/g, " "),
  });
}

function auditEntryRange(entry, t) {
  if (entry?.work_date) return entry.work_date;
  if (entry?.effective_from || entry?.effective_to) {
    return formatDateRange(entry.effective_from, entry.effective_to, t);
  }
  return "";
}


function shiftDateOnly(dateOnly, dayOffset) {
  const base = parseDateOnly(dateOnly);
  if (!base) return "";
  return toDateOnly(addDays(base, Number(dayOffset || 0)));
}

function getPreviewDayByDate(row, dateOnly) {
  return (row?.days || []).find((day) => day.work_date === dateOnly) || null;
}

function buildCopyReason(prefix, sourceUserTitle, sourceDate) {
  // Keep this backend payload simple and language-neutral.  It is an operational
  // audit hint, not a user-facing translated sentence.  Reception can still
  // replace the reason later through the normal daily-override dialog if needed.
  return [prefix, sourceUserTitle, sourceDate].filter(Boolean).join(" · ");
}

/* -------------------------------------------------------------------------- */
/* Small presentational helpers                                                */
/* -------------------------------------------------------------------------- */

function EmployeeMultiSelect({ employees, selectedIds, onChange, labels }) {
  const selectedSet = useMemo(() => new Set((selectedIds || []).map(Number)), [selectedIds]);

  const toggle = (id) => {
    const numeric = Number(id);
    const next = new Set(selectedSet);
    if (next.has(numeric)) next.delete(numeric);
    else next.add(numeric);
    onChange(Array.from(next));
  };

  const selectAll = () => onChange(employees.map((e) => Number(e.id)).filter(Boolean));
  const clear = () => onChange([]);

  return (
    <div className="hrws-employee-picker">
      <div className="hrws-employee-picker__actions">
        <button type="button" className="hrws-link-button" onClick={selectAll}>
          {labels.selectAll}
        </button>
        <button type="button" className="hrws-link-button" onClick={clear}>
          {labels.clear}
        </button>
      </div>

      <div className="hrws-employee-picker__list">
        {employees.map((employee) => (
          <label key={employee.id} className="hrws-employee-chip">
            <input
              type="checkbox"
              checked={selectedSet.has(Number(employee.id))}
              onChange={() => toggle(employee.id)}
            />
            <span>{employee.title || employee.username || `User ${employee.id}`}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function SourceBadge({ source, t }) {
  const key = normalizeSource(source);
  const label = sourceLabel(key, t);

  return (
    <span className={`hrws-source-badge hrws-source-badge--${key.toLowerCase().replace(/_/g, "-")}`}>
      {label}
    </span>
  );
}

function DayCell({
  user,
  day,
  value,
  onOpen,
  onPreset,
  onCopyDay,
  quickEditMode = false,
  quickSavingKey = "",
  t,
  dimmed = false,
}) {
  const source = normalizeSource(value?.source);
  const editable = isSourceEditable(source);
  const warnings = Array.isArray(value?.warnings) ? value.warnings : [];
  const absences = Array.isArray(value?.absences) ? value.absences : [];
  const warningSeverity = warningSeverityClass(warnings);
  const tooltip = buildDayTooltip({ user, day, value, t });
  const cellKey = `${Number(user?.id)}:${day.dateOnly}`;
  const isQuickSaving = quickSavingKey === cellKey;

  return (
    <div
      className={`hrws-day-cell hrws-day-cell--${source.toLowerCase().replace(/_/g, "-")} ${day.isWeekend ? "hrws-day-cell--weekend" : ""} ${dimmed ? "hrws-day-cell--dimmed" : ""} ${quickEditMode ? "hrws-day-cell--quick" : ""} ${absences.length > 0 ? "hrws-day-cell--has-absence" : ""} ${warnings.length > 0 ? `hrws-day-cell--warning-${warningSeverity}` : ""}`}
      title={tooltip}
    >
      <button
        type="button"
        className="hrws-day-cell__main"
        onClick={() => onOpen(user, day, value)}
      >
        <span className="hrws-day-cell__minutes">{formatMinutes(value?.minutes || 0, t)}</span>
        <SourceBadge source={source} t={t} />
        {absences.length > 0 && (
          <span
            className="hrws-absence-dot"
            aria-label={t("leaves:workSchedules.absenceCount", { count: absences.length })}
            title={t("leaves:workSchedules.absenceCount", { count: absences.length })}
          >
            {t("leaves:workSchedules.absenceBadge", { defaultValue: "A" })}
          </span>
        )}
        {warnings.length > 0 && (
          <span
            className={`hrws-warning-dot hrws-warning-dot--${warningSeverity}`}
            aria-label={t("leaves:workSchedules.warningCount", { count: warnings.length })}
          >
            !
          </span>
        )}
        {!editable && (
          <span className="hrws-day-cell__lock" aria-hidden="true">●</span>
        )}
      </button>

      {quickEditMode && editable && (
        <div className="hrws-quick-presets" aria-label={t("leaves:workSchedules.quickPresets")}>
          {QUICK_EDIT_PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              className={Number(value?.minutes || 0) === preset ? "is-active" : ""}
              disabled={isQuickSaving}
              onClick={(event) => {
                event.stopPropagation();
                onPreset(user, day, value, preset);
              }}
              title={t("leaves:workSchedules.quickPresetTitle", { value: formatMinutes(preset, t) })}
            >
              {formatMinutes(preset, t)}
            </button>
          ))}
          <button
            type="button"
            className="hrws-quick-presets__copy"
            disabled={isQuickSaving}
            onClick={(event) => {
              event.stopPropagation();
              onCopyDay(user, day, value);
            }}
            title={t("leaves:workSchedules.copyDayToEmployees")}
          >
            ⧉
          </button>
        </div>
      )}
    </div>
  );
}


function WorkPatternSummary({ assignment, onManage, t }) {
  const template = assignment?.template;

  return (
    <div className="hrws-pattern-summary">
      <span className="hrws-pattern-summary__label">
        {t("leaves:workSchedules.currentPattern")}
      </span>
      {template ? (
        <>
          <strong>{template.name}</strong>
          <small>{formatDateRange(assignment.effective_from, assignment.effective_to, t)}</small>
        </>
      ) : (
        <small>{t("leaves:workSchedules.noPatternAssigned")}</small>
      )}
      <button type="button" className="hrws-link-button" onClick={onManage}>
        {t("leaves:workSchedules.managePattern")}
      </button>
    </div>
  );
}

function WorkPatternMiniPreview({ pattern, t }) {
  const days = normalizePatternDays(pattern?.days);
  if (!pattern || days.length === 0) {
    return <p className="hrws-muted-text">{t("leaves:workSchedules.patternNoDayRows")}</p>;
  }

  const byWeek = new Map();
  for (const day of days) {
    if (!byWeek.has(day.week_index)) byWeek.set(day.week_index, []);
    byWeek.get(day.week_index).push(day);
  }

  return (
    <div className="hrws-pattern-preview">
      {Array.from(byWeek.entries()).map(([weekIndex, weekDays]) => (
        <div key={weekIndex} className="hrws-pattern-preview__week">
          <strong>{t("leaves:workSchedules.patternWeek", { week: weekIndex })}</strong>
          <div>
            {weekDays.map((day) => (
              <span key={`${day.week_index}:${day.day_of_week}`}>
                {t(`leaves:workSchedules.patternDayNames.${day.day_of_week}`, { defaultValue: String(day.day_of_week) })}: {formatMinutes(day.minutes, t)}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Dialogs                                                                     */
/* -------------------------------------------------------------------------- */

function OverrideDialog({ state, onClose, onSave, onDelete, saving, t }) {
  const [minutes, setMinutes] = useState(state ? String(state.minutes ?? 0) : "0");
  const [reason, setReason] = useState(state?.reason || "");

  useEffect(() => {
    if (!state) return;
    setMinutes(String(state.minutes ?? 0));
    setReason(state.reason || "");
  }, [state]);

  if (!state) return null;

  const source = String(state.dayData?.source || "").toUpperCase();
  const overrideId = normalizeSourceRef(state.dayData?.source_ref);
  const canDelete = source === "EMPLOYEE_OVERRIDE" && overrideId;
  const canSave = isSourceEditable(source);
  const reasonMissing = canSave && reason.trim().length === 0;

  const submit = (event) => {
    event.preventDefault();
    const value = Number(minutes);
    if (reasonMissing) return;
    onSave({
      userId: state.user.id,
      work_date: state.day.dateOnly,
      minutes: Number.isInteger(value) ? value : Number.NaN,
      reason,
    });
  };

  return (
    <div className="hrws-dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <div
        className="hrws-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="hrws-dialog-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="hrws-dialog__header">
          <div>
            <h2 id="hrws-dialog-title">{t("leaves:workSchedules.dialogTitle")}</h2>
            <p>{state.user.title} · {state.day.dateOnly}</p>
          </div>
          <button type="button" className="hrws-icon-button" onClick={onClose} aria-label={t("common:close", { defaultValue: "Close" })}>
            ×
          </button>
        </div>

        <div className="hrws-dialog__current">
          <span>{t("leaves:workSchedules.currentValue")}</span>
          <strong>{formatMinutes(state.dayData?.minutes || 0, t)}</strong>
          <SourceBadge source={source} t={t} />
          {state.dayData?.note && <small>{state.dayData.note}</small>}
        </div>

        {!canSave && (
          <div className="hrws-warning-box">
            {t("leaves:workSchedules.facilityExceptionWarning")}
          </div>
        )}

        <form onSubmit={submit} className="hrws-dialog__form">
          <label>
            <span>{t("leaves:workSchedules.minutes")}</span>
            <input
              type="number"
              min="0"
              max="1440"
              step="1"
              value={minutes}
              disabled={!canSave || saving}
              onChange={(event) => setMinutes(event.target.value)}
            />
            <small>{t("leaves:workSchedules.minutesHelp")}</small>
          </label>

          <label>
            <span>{t("leaves:workSchedules.reason")}</span>
            <textarea
              rows="3"
              value={reason}
              disabled={!canSave || saving}
              onChange={(event) => setReason(event.target.value)}
              placeholder={t("leaves:workSchedules.reasonPlaceholder")}
            />
            <small className={reasonMissing ? "hrws-field-warning" : ""}>
              {reasonMissing
                ? t("leaves:workSchedules.reasonRequired")
                : t("leaves:workSchedules.reasonRecommended")}
            </small>
          </label>

          <div className="hrws-dialog__actions">
            {canDelete && (
              <button
                type="button"
                className="hrws-danger-button"
                disabled={saving}
                onClick={() => onDelete(state.user.id, overrideId)}
              >
                {t("leaves:workSchedules.deleteOverride")}
              </button>
            )}
            <span className="hrws-dialog__spacer" />
            <button type="button" className="hrws-secondary-button" onClick={onClose} disabled={saving}>
              {t("common:cancel", { defaultValue: "Cancel" })}
            </button>
            <button type="submit" className="hrws-primary-button" disabled={!canSave || saving || reasonMissing}>
              {saving ? t("common:saving", { defaultValue: "Saving..." }) : t("leaves:workSchedules.saveOverride")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PatternAssignmentDialog({ state, workPatterns, onClose, onSave, onDelete, saving, loading, t }) {
  const current = state?.currentAssignment;
  const [templateId, setTemplateId] = useState(current?.template_id ? String(current.template_id) : "");
  const [effectiveFrom, setEffectiveFrom] = useState(current?.effective_from || state?.defaultEffectiveFrom || "");
  const [effectiveTo, setEffectiveTo] = useState(current?.effective_to || "");
  const [cycleStartDate, setCycleStartDate] = useState(current?.cycle_start_date || current?.effective_from || state?.defaultEffectiveFrom || "");

  useEffect(() => {
    if (!state) return;
    setTemplateId(state.currentAssignment?.template_id ? String(state.currentAssignment.template_id) : "");
    setEffectiveFrom(state.currentAssignment?.effective_from || state.defaultEffectiveFrom || "");
    setEffectiveTo(state.currentAssignment?.effective_to || "");
    setCycleStartDate(state.currentAssignment?.cycle_start_date || state.currentAssignment?.effective_from || state.defaultEffectiveFrom || "");
  }, [state]);

  if (!state) return null;

  const selectedPattern = workPatterns.find((pattern) => Number(pattern.id) === Number(templateId));
  const activeAssignments = (state.assignments || []).filter((item) => item.active);
  const history = state.assignments || [];

  const submit = (event) => {
    event.preventDefault();
    onSave(state.user.id, {
      template_id: Number(templateId),
      effective_from: effectiveFrom,
      effective_to: effectiveTo || null,
      cycle_start_date: cycleStartDate || effectiveFrom,
    });
  };

  return (
    <div className="hrws-dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <div
        className="hrws-dialog hrws-pattern-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="hrws-pattern-dialog-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="hrws-dialog__header">
          <div>
            <h2 id="hrws-pattern-dialog-title">{t("leaves:workSchedules.patternDialogTitle")}</h2>
            <p>{state.user.title}</p>
          </div>
          <button type="button" className="hrws-icon-button" onClick={onClose} aria-label={t("common:close", { defaultValue: "Close" })}>
            ×
          </button>
        </div>

        <div className="hrws-pattern-dialog__body">
          <form onSubmit={submit} className="hrws-dialog__form hrws-pattern-form">
            <label>
              <span>{t("leaves:workSchedules.patternTemplate")}</span>
              <select value={templateId} disabled={saving || loading} onChange={(event) => setTemplateId(event.target.value)}>
                <option value="">{t("leaves:workSchedules.selectPattern")}</option>
                {workPatterns.map((pattern) => (
                  <option key={pattern.id} value={pattern.id}>
                    {pattern.name} ({pattern.code})
                  </option>
                ))}
              </select>
              <small>{t("leaves:workSchedules.patternTemplateHelp")}</small>
            </label>

            <div className="hrws-pattern-form__dates">
              <label>
                <span>{t("leaves:workSchedules.effectiveFrom")}</span>
                <input type="date" value={effectiveFrom} disabled={saving || loading} onChange={(event) => setEffectiveFrom(event.target.value)} />
              </label>
              <label>
                <span>{t("leaves:workSchedules.effectiveTo")}</span>
                <input type="date" value={effectiveTo || ""} disabled={saving || loading} onChange={(event) => setEffectiveTo(event.target.value)} />
              </label>
              <label>
                <span>{t("leaves:workSchedules.cycleStartDate")}</span>
                <input type="date" value={cycleStartDate} disabled={saving || loading} onChange={(event) => setCycleStartDate(event.target.value)} />
              </label>
            </div>

            <WorkPatternMiniPreview pattern={selectedPattern} t={t} />

            <div className="hrws-dialog__actions">
              <span className="hrws-dialog__spacer" />
              <button type="button" className="hrws-secondary-button" onClick={onClose} disabled={saving}>
                {t("common:cancel", { defaultValue: "Cancel" })}
              </button>
              <button type="submit" className="hrws-primary-button" disabled={!templateId || !effectiveFrom || !cycleStartDate || saving || loading}>
                {saving ? t("common:saving", { defaultValue: "Saving..." }) : t("leaves:workSchedules.savePatternAssignment")}
              </button>
            </div>
          </form>

          <aside className="hrws-pattern-history">
            <h3>{t("leaves:workSchedules.patternHistory")}</h3>
            {loading && <p className="hrws-muted-text">{t("common:loading", { defaultValue: "Loading..." })}</p>}
            {!loading && history.length === 0 && (
              <p className="hrws-muted-text">{t("leaves:workSchedules.noPatternHistory")}</p>
            )}
            {!loading && history.map((assignment) => (
              <article key={assignment.id} className={`hrws-pattern-history__item ${assignment.active ? "hrws-pattern-history__item--active" : ""}`}>
                <div>
                  <strong>{assignment.template?.name || t("leaves:workSchedules.unknownPattern")}</strong>
                  <span>{formatDateRange(assignment.effective_from, assignment.effective_to, t)}</span>
                  <small>
                    {t("leaves:workSchedules.cycleStartDate")}: {assignment.cycle_start_date || "—"}
                  </small>
                </div>
                <span className="hrws-pattern-history__state">
                  {assignment.active
                    ? t("leaves:workSchedules.assignmentActive")
                    : t("leaves:workSchedules.assignmentInactive")}
                </span>
                {assignment.active && activeAssignments.length > 0 && (
                  <button
                    type="button"
                    className="hrws-danger-button hrws-pattern-history__delete"
                    disabled={saving}
                    onClick={() => onDelete(state.user.id, assignment.id)}
                  >
                    {t("leaves:workSchedules.clearPatternAssignment")}
                  </button>
                )}
              </article>
            ))}
          </aside>
        </div>
      </div>
    </div>
  );
}


function ExactScheduleDialog({ state, employees, selectedUserIds, onClose, onSave, onBulkSave, onDelete, saving, loading, t }) {
  const initialDays = state?.days || buildEmptyWeekDays();
  const [mode, setMode] = useState(state?.mode || "single");
  const [effectiveFrom, setEffectiveFrom] = useState(state?.effective_from || state?.defaultEffectiveFrom || "");
  const [effectiveTo, setEffectiveTo] = useState(state?.effective_to || state?.defaultEffectiveTo || "");
  const [days, setDays] = useState(initialDays);
  const [bulkUserIds, setBulkUserIds] = useState(state?.user?.id ? [Number(state.user.id)] : []);

  useEffect(() => {
    if (!state) return;
    setMode(state.mode || "single");
    setEffectiveFrom(state.effective_from || state.defaultEffectiveFrom || "");
    setEffectiveTo(state.effective_to || state.defaultEffectiveTo || "");
    setDays(state.days || buildEmptyWeekDays());
    setBulkUserIds(state.user?.id ? [Number(state.user.id)] : selectedUserIds);
  }, [selectedUserIds, state]);

  if (!state) return null;

  const updateMinutes = (dow, value) => {
    const n = Number(value);
    setDays((current) => current.map((day) => (
      Number(day.day_of_week) === Number(dow)
        ? { ...day, minutes: Number.isFinite(n) ? Math.max(0, Math.min(1440, Math.round(n))) : 0 }
        : day
    )));
  };

  const applyCommonPreset = (minutes) => {
    setDays(buildEmptyWeekDays().map((day) => ({ ...day, minutes: day.day_of_week <= 5 ? minutes : 0 })));
  };

  const submit = (event) => {
    event.preventDefault();
    const payload = {
      effective_from: effectiveFrom,
      effective_to: effectiveTo || null,
      days,
    };
    if (mode === "bulk") {
      onBulkSave({ ...payload, user_ids: bulkUserIds });
    } else {
      onSave(state.user.id, payload);
    }
  };

  return (
    <div className="hrws-dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <div
        className="hrws-dialog hrws-exact-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="hrws-exact-dialog-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="hrws-dialog__header">
          <div>
            <h2 id="hrws-exact-dialog-title">{t("leaves:workSchedules.exactDialogTitle")}</h2>
            <p>{state.user?.title || t("leaves:workSchedules.bulkApply")}</p>
          </div>
          <button type="button" className="hrws-icon-button" onClick={onClose} aria-label={t("common:close", { defaultValue: "Close" })}>
            ×
          </button>
        </div>

        <form onSubmit={submit} className="hrws-dialog__form hrws-exact-form">
          <div className="hrws-segmented">
            <button type="button" className={mode === "single" ? "is-active" : ""} onClick={() => setMode("single")}>{t("leaves:workSchedules.singleEmployee")}</button>
            <button type="button" className={mode === "bulk" ? "is-active" : ""} onClick={() => setMode("bulk")}>{t("leaves:workSchedules.bulkApply")}</button>
          </div>

          {mode === "bulk" && (
            <div className="hrws-exact-form__employees">
              <h3>{t("leaves:workSchedules.bulkEmployees")}</h3>
              <EmployeeMultiSelect
                employees={employees}
                selectedIds={bulkUserIds}
                onChange={setBulkUserIds}
                labels={{
                  selectAll: t("leaves:workSchedules.selectAll"),
                  clear: t("leaves:workSchedules.clearSelection"),
                }}
              />
            </div>
          )}

          <div className="hrws-pattern-form__dates">
            <label>
              <span>{t("leaves:workSchedules.effectiveFrom")}</span>
              <input type="date" value={effectiveFrom} disabled={saving || loading} onChange={(event) => setEffectiveFrom(event.target.value)} />
            </label>
            <label>
              <span>{t("leaves:workSchedules.effectiveTo")}</span>
              <input type="date" value={effectiveTo || ""} disabled={saving || loading} onChange={(event) => setEffectiveTo(event.target.value)} />
            </label>
          </div>

          <div className="hrws-exact-presets">
            <button type="button" className="hrws-secondary-button" onClick={() => applyCommonPreset(480)}>{t("leaves:workSchedules.presetFullTime")}</button>
            <button type="button" className="hrws-secondary-button" onClick={() => applyCommonPreset(240)}>{t("leaves:workSchedules.presetHalfTime")}</button>
            <button type="button" className="hrws-secondary-button" onClick={() => setDays(buildEmptyWeekDays())}>{t("leaves:workSchedules.presetFreeWeek")}</button>
          </div>

          <div className="hrws-week-editor">
            {days.map((day) => (
              <label key={day.day_of_week}>
                <span>{formatIsoDayLabel(day.day_of_week, t)}</span>
                <input
                  type="number"
                  min="0"
                  max="1440"
                  step="1"
                  value={day.minutes}
                  disabled={saving || loading}
                  onChange={(event) => updateMinutes(day.day_of_week, event.target.value)}
                />
                <small>{formatMinutes(day.minutes, t)}</small>
              </label>
            ))}
          </div>

          {Array.isArray(state.warnings) && state.warnings.length > 0 && (
            <div className="hrws-warning-box hrws-warning-box--stacked">
              <strong>{t("leaves:workSchedules.conflictWarnings")}</strong>
              <ul>
                {state.warnings.slice(0, 8).map((warning, index) => (
                  <li key={`${warning.type}:${index}`}>{warning.message || warning.type}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="hrws-dialog__actions">
            {mode === "single" && state.schedule?.effective_from && (
              <button
                type="button"
                className="hrws-danger-button"
                disabled={saving || loading}
                onClick={() => onDelete(state.user.id, state.schedule.effective_from)}
              >
                {t("leaves:workSchedules.deleteExactSchedule")}
              </button>
            )}
            <span className="hrws-dialog__spacer" />
            <button type="button" className="hrws-secondary-button" onClick={onClose} disabled={saving}>
              {t("common:cancel", { defaultValue: "Cancel" })}
            </button>
            <button type="submit" className="hrws-primary-button" disabled={!effectiveFrom || saving || loading || (mode === "bulk" && bulkUserIds.length === 0)}>
              {saving ? t("common:saving", { defaultValue: "Saving..." }) : t("leaves:workSchedules.saveExactSchedule")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CopyDayDialog({ state, employees, selectedUserIds, onClose, onApply, saving, t }) {
  const [targetUserIds, setTargetUserIds] = useState([]);
  const [minutes, setMinutes] = useState(0);
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!state) return;
    const sourceUserId = Number(state.user?.id || 0);
    const defaultTargets = (selectedUserIds || []).filter((id) => Number(id) !== sourceUserId);
    setTargetUserIds(defaultTargets.length ? defaultTargets : [sourceUserId].filter(Boolean));
    setMinutes(Number(state.value?.minutes || 0));
    setReason(buildCopyReason("Copied day", state.user?.title, state.day?.dateOnly));
  }, [selectedUserIds, state]);

  if (!state) return null;

  const submit = (event) => {
    event.preventDefault();
    onApply({
      targetUserIds,
      work_date: state.day.dateOnly,
      minutes: Number(minutes),
      reason,
    });
  };

  return (
    <div className="hrws-dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <div
        className="hrws-dialog hrws-copy-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="hrws-copy-day-dialog-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="hrws-dialog__header">
          <div>
            <h2 id="hrws-copy-day-dialog-title">{t("leaves:workSchedules.copyDayDialogTitle")}</h2>
            <p>{state.user.title} · {state.day.dateOnly} · {formatMinutes(state.value?.minutes || 0, t)}</p>
          </div>
          <button type="button" className="hrws-icon-button" onClick={onClose} aria-label={t("common:close", { defaultValue: "Close" })}>
            ×
          </button>
        </div>

        <form onSubmit={submit} className="hrws-dialog__form hrws-copy-form">
          <label>
            <span>{t("leaves:workSchedules.minutes")}</span>
            <input
              type="number"
              min="0"
              max="1440"
              step="1"
              value={minutes}
              disabled={saving}
              onChange={(event) => setMinutes(event.target.value)}
            />
            <small>{t("leaves:workSchedules.copyDayMinutesHelp")}</small>
          </label>

          <label>
            <span>{t("leaves:workSchedules.reason")}</span>
            <textarea
              rows="2"
              value={reason}
              disabled={saving}
              onChange={(event) => setReason(event.target.value)}
            />
          </label>

          <div className="hrws-copy-form__employees">
            <h3>{t("leaves:workSchedules.targetEmployees")}</h3>
            <EmployeeMultiSelect
              employees={employees}
              selectedIds={targetUserIds}
              onChange={setTargetUserIds}
              labels={{
                selectAll: t("leaves:workSchedules.selectAll"),
                clear: t("leaves:workSchedules.clearSelection"),
              }}
            />
          </div>

          <div className="hrws-dialog__actions">
            <span className="hrws-dialog__spacer" />
            <button type="button" className="hrws-secondary-button" onClick={onClose} disabled={saving}>
              {t("common:cancel", { defaultValue: "Cancel" })}
            </button>
            <button type="submit" className="hrws-primary-button" disabled={saving || targetUserIds.length === 0}>
              {saving ? t("common:saving", { defaultValue: "Saving..." }) : t("leaves:workSchedules.applyCopyDay")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CopyWeekDialog({ state, employees, selectedUserIds, weekStart, onClose, onApply, saving, t }) {
  const [targetUserIds, setTargetUserIds] = useState([]);
  const [targetWeekStart, setTargetWeekStart] = useState(shiftDateOnly(weekStart, 7));
  const [repeatWeeks, setRepeatWeeks] = useState(1);
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!state) return;
    const sourceUserId = Number(state.row?.user?.id || 0);
    setTargetUserIds([sourceUserId].filter(Boolean));
    setTargetWeekStart(shiftDateOnly(weekStart, 7));
    setRepeatWeeks(1);
    setReason(buildCopyReason("Copied week", state.row?.user?.title, weekStart));
  }, [state, selectedUserIds, weekStart]);

  if (!state) return null;

  const sourceDays = (state.row?.days || []).filter((day) => normalizeSource(day.source) !== "FACILITY_EXCEPTION");
  const sourceTotal = sourceDays.reduce((sum, day) => sum + Number(day.minutes || 0), 0);

  const submit = (event) => {
    event.preventDefault();
    onApply({
      sourceRow: state.row,
      targetUserIds,
      targetWeekStart,
      repeatWeeks: Number(repeatWeeks),
      reason,
    });
  };

  return (
    <div className="hrws-dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <div
        className="hrws-dialog hrws-copy-dialog hrws-copy-week-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="hrws-copy-week-dialog-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="hrws-dialog__header">
          <div>
            <h2 id="hrws-copy-week-dialog-title">{t("leaves:workSchedules.copyWeekDialogTitle")}</h2>
            <p>{state.row?.user?.title} · {weekStart} – {shiftDateOnly(weekStart, 6)} · {formatMinutes(sourceTotal, t)}</p>
          </div>
          <button type="button" className="hrws-icon-button" onClick={onClose} aria-label={t("common:close", { defaultValue: "Close" })}>
            ×
          </button>
        </div>

        <form onSubmit={submit} className="hrws-dialog__form hrws-copy-form">
          <div className="hrws-pattern-form__dates">
            <label>
              <span>{t("leaves:workSchedules.targetWeekStart")}</span>
              <input
                type="date"
                value={targetWeekStart}
                disabled={saving}
                onChange={(event) => setTargetWeekStart(toDateOnly(startOfWeekMonday(event.target.value)))}
              />
              <small>{t("leaves:workSchedules.targetWeekStartHelp")}</small>
            </label>
            <label>
              <span>{t("leaves:workSchedules.repeatWeeks")}</span>
              <input
                type="number"
                min="1"
                max="12"
                step="1"
                value={repeatWeeks}
                disabled={saving}
                onChange={(event) => setRepeatWeeks(event.target.value)}
              />
              <small>{t("leaves:workSchedules.repeatWeeksHelp")}</small>
            </label>
          </div>

          <label>
            <span>{t("leaves:workSchedules.reason")}</span>
            <textarea
              rows="2"
              value={reason}
              disabled={saving}
              onChange={(event) => setReason(event.target.value)}
            />
          </label>

          <div className="hrws-copy-week-preview">
            {sourceDays.map((day) => (
              <span key={day.work_date}>{day.work_date}: {formatMinutes(day.minutes || 0, t)}</span>
            ))}
            {sourceDays.length !== (state.row?.days || []).length && (
              <em>{t("leaves:workSchedules.copyWeekFacilitySkipped")}</em>
            )}
          </div>

          <div className="hrws-copy-form__employees">
            <h3>{t("leaves:workSchedules.targetEmployees")}</h3>
            <EmployeeMultiSelect
              employees={employees}
              selectedIds={targetUserIds}
              onChange={setTargetUserIds}
              labels={{
                selectAll: t("leaves:workSchedules.selectAll"),
                clear: t("leaves:workSchedules.clearSelection"),
              }}
            />
          </div>

          <div className="hrws-dialog__actions">
            <span className="hrws-dialog__spacer" />
            <button type="button" className="hrws-secondary-button" onClick={onClose} disabled={saving}>
              {t("common:cancel", { defaultValue: "Cancel" })}
            </button>
            <button type="submit" className="hrws-primary-button" disabled={saving || targetUserIds.length === 0 || !targetWeekStart || Number(repeatWeeks) < 1}>
              {saving ? t("common:saving", { defaultValue: "Saving..." }) : t("leaves:workSchedules.applyCopyWeek")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


function AuditDialog({ state, onClose, loading, t }) {
  if (!state) return null;

  const user = state.user || {};
  const entries = Array.isArray(state.entries) ? state.entries : [];

  return (
    <div className="hrws-dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <div
        className="hrws-dialog hrws-dialog--wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="hrws-audit-dialog-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="hrws-dialog__header">
          <div>
            <h2 id="hrws-audit-dialog-title">{t("leaves:workSchedules.audit.title")}</h2>
            <p>{user.title || `User ${user.id}`} · {state.from} – {state.to}</p>
          </div>
          <button type="button" className="hrws-icon-button" onClick={onClose} aria-label={t("common:close", { defaultValue: "Close" })}>
            ×
          </button>
        </div>

        <p className="hrws-dialog-help">
          {t("leaves:workSchedules.audit.help")}
        </p>

        {loading ? (
          <div className="hrws-audit-empty">{t("common:loading", { defaultValue: "Loading..." })}</div>
        ) : entries.length === 0 ? (
          <div className="hrws-audit-empty">{t("leaves:workSchedules.audit.empty")}</div>
        ) : (
          <div className="hrws-audit-list">
            {entries.map((entry) => (
              <article key={entry.id || entry.reference} className={`hrws-audit-entry hrws-audit-entry--${String(entry.scope || "schedule").toLowerCase().replace(/_/g, "-")}`}>
                <div className="hrws-audit-entry__main">
                  <strong>{auditEntryLabel(entry, t)}</strong>
                  <span>{auditEntryRange(entry, t)}</span>
                  {entry.label && <small>{entry.label}</small>}
                  {entry.reason && <small>{t("leaves:workSchedules.reason")}: {entry.reason}</small>}
                </div>
                <div className="hrws-audit-entry__meta">
                  {Number.isFinite(Number(entry.minutes)) && entry.minutes !== null && (
                    <span>{formatMinutes(entry.minutes, t)}</span>
                  )}
                  <span>{entry.active ? t("leaves:workSchedules.audit.active") : t("leaves:workSchedules.audit.inactive")}</span>
                  <span>{formatDateTime(entry.updatedAt || entry.createdAt)}</span>
                  {entry.actor?.name && <span>{entry.actor.name}</span>}
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Main page                                                                  */
/* -------------------------------------------------------------------------- */

const WorkSchedulesPage = () => {
  const { t } = useTranslation(["leaves", "navigation", "common"]);

  const savedView = useMemo(() => readSavedWorkScheduleView(), []);
  const initialWeekStart = useMemo(() => toDateOnly(startOfWeekMonday(new Date())), []);
  const [weekStart, setWeekStart] = useState(savedView.weekStart || initialWeekStart);
  const [employees, setEmployees] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [preview, setPreview] = useState({ rows: [], users: [], range: null, meta: {} });
  const [workPatterns, setWorkPatterns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [patternLoading, setPatternLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [dialogState, setDialogState] = useState(null);
  const [patternDialogState, setPatternDialogState] = useState(null);
  const [exactDialogState, setExactDialogState] = useState(null);
  const [exactLoading, setExactLoading] = useState(false);
  const [showOnlyExceptions, setShowOnlyExceptions] = useState(Boolean(savedView.showOnlyExceptions));
  const [quickEditMode, setQuickEditMode] = useState(Boolean(savedView.quickEditMode));
  const [quickSavingKey, setQuickSavingKey] = useState("");
  const [copyDayState, setCopyDayState] = useState(null);
  const [copyWeekState, setCopyWeekState] = useState(null);
  const [auditDialogState, setAuditDialogState] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);

  const weekDays = useMemo(() => buildWeekDays(weekStart), [weekStart]);
  const weekEnd = weekDays.length ? weekDays[weekDays.length - 1].dateOnly : weekStart;

  const employeeLabels = useMemo(() => ({
    selectAll: t("leaves:workSchedules.selectAll", { defaultValue: "Select all" }),
    clear: t("leaves:workSchedules.clearSelection", { defaultValue: "Clear" }),
  }), [t]);

  const loadEmployees = useCallback(async () => {
    const rows = await fetchEmployees();
    const normalized = (Array.isArray(rows) ? rows : []).map((row) => ({
      id: Number(row.id),
      title: row.title || row.username || `User ${row.id}`,
      role: row.role,
    })).filter((row) => Number.isInteger(row.id) && row.id > 0);

    setEmployees(normalized);
    setSelectedUserIds((current) => {
      if (Array.isArray(current) && current.length) return current;
      const savedIds = Array.isArray(savedView.userIds)
        ? savedView.userIds.map(Number).filter((id) => normalized.some((row) => row.id === id))
        : [];
      return savedIds.length ? savedIds : normalized.map((row) => row.id);
    });
  }, [savedView.userIds]);

  const loadWorkPatterns = useCallback(async ({ signal } = {}) => {
    const rows = await fetchWorkPatterns({ signal });
    setWorkPatterns(Array.isArray(rows) ? rows : []);
  }, []);

  const loadPreview = useCallback(async ({ signal } = {}) => {
    if (!weekStart || !weekEnd) return;

    setLoading(true);
    setError("");
    try {
      const data = await fetchWorkSchedulePreview(
        { from: weekStart, to: weekEnd, userIds: selectedUserIds },
        { signal }
      );
      setPreview(data);
    } catch (err) {
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
      setError(err?.message || t("leaves:workSchedules.loadError"));
    } finally {
      setLoading(false);
    }
  }, [selectedUserIds, t, weekEnd, weekStart]);

  useEffect(() => {
    loadEmployees().catch((err) => {
      console.error("[WorkSchedulesPage] failed to load employees", err);
      setError(err?.message || t("leaves:workSchedules.loadEmployeesError"));
    });
  }, [loadEmployees, t]);

  useEffect(() => {
    const controller = new AbortController();
    loadWorkPatterns({ signal: controller.signal }).catch((err) => {
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
      setError(err?.message || t("leaves:workSchedules.loadPatternsError"));
    });
    return () => controller.abort();
  }, [loadWorkPatterns, t]);

  useEffect(() => {
    const controller = new AbortController();
    loadPreview({ signal: controller.signal });
    return () => controller.abort();
  }, [loadPreview]);

  useEffect(() => {
    writeSavedWorkScheduleView({
      weekStart,
      userIds: selectedUserIds,
      showOnlyExceptions,
      quickEditMode,
    });
  }, [quickEditMode, selectedUserIds, showOnlyExceptions, weekStart]);


  const previewRows = Array.isArray(preview.rows) ? preview.rows : [];

  const byUserAndDate = useMemo(() => {
    const map = new Map();
    for (const row of previewRows) {
      const userId = Number(row.user?.id);
      for (const day of row.days || []) {
        map.set(`${userId}:${day.work_date}`, day);
      }
    }
    return map;
  }, [previewRows]);

  const rowHasExceptionOrConflict = useCallback((row) => (row?.days || []).some((day) => (
    hasExceptionOrConflictDay(day)
  )), []);

  const visibleRows = useMemo(() => {
    if (!showOnlyExceptions) return previewRows;
    return previewRows.filter(rowHasExceptionOrConflict);
  }, [previewRows, rowHasExceptionOrConflict, showOnlyExceptions]);

  const dayTotals = useMemo(() => weekDays.map((day) => {
    let minutes = 0;
    let people = 0;
    let exceptions = 0;

    for (const row of visibleRows) {
      const userId = Number(row.user?.id);
      const value = byUserAndDate.get(`${userId}:${day.dateOnly}`);
      const dayMinutes = Number(value?.minutes || 0);
      minutes += dayMinutes;
      if (dayMinutes > 0) people += 1;
      if (hasExceptionOrConflictDay(value)) exceptions += 1;
    }

    return { dateOnly: day.dateOnly, minutes, people, exceptions };
  }), [byUserAndDate, visibleRows, weekDays]);

  const visibleWeekTotalMinutes = useMemo(() => dayTotals.reduce((sum, item) => (
    sum + Number(item.minutes || 0)
  ), 0), [dayTotals]);

  const visibleExceptionCellCount = useMemo(() => dayTotals.reduce((sum, item) => (
    sum + Number(item.exceptions || 0)
  ), 0), [dayTotals]);

  const warningSummary = preview?.meta?.warningSummary || { total: 0, byType: [] };
  const warningTypes = Array.isArray(warningSummary.byType) ? warningSummary.byType : [];

  const goWeek = (delta) => {
    const current = parseDateOnly(weekStart) || startOfWeekMonday(new Date());
    setWeekStart(toDateOnly(addDays(current, delta * 7)));
  };

  const goToday = () => {
    setWeekStart(toDateOnly(startOfWeekMonday(new Date())));
  };

  const openDialog = (user, day, dayData) => {
    setSuccess("");
    setError("");
    setDialogState({
      user,
      day,
      dayData,
      minutes: dayData?.minutes || 0,
      reason: String(dayData?.source || "").toUpperCase() === "EMPLOYEE_OVERRIDE"
        ? (dayData?.note || "")
        : "",
    });
  };

  const openPatternDialog = async (row) => {
    const user = row.user || {};
    setSuccess("");
    setError("");
    setPatternLoading(true);
    setPatternDialogState({
      user,
      currentAssignment: row.workPatternAssignment || null,
      assignments: [],
      defaultEffectiveFrom: weekStart,
    });

    try {
      const assignments = await fetchPatternAssignments(user.id);
      const active = assignments.find((item) => item.active) || row.workPatternAssignment || null;
      setPatternDialogState({
        user,
        currentAssignment: active,
        assignments,
        defaultEffectiveFrom: weekStart,
      });
    } catch (err) {
      setError(err?.message || t("leaves:workSchedules.patternAssignmentsLoadError"));
    } finally {
      setPatternLoading(false);
    }
  };

  const refreshPatternDialogAssignments = async (user) => {
    const assignments = await fetchPatternAssignments(user.id);
    const active = assignments.find((item) => item.active) || null;
    setPatternDialogState((current) => current ? {
      ...current,
      currentAssignment: active,
      assignments,
    } : current);
  };

  const handleSaveOverride = async ({ userId, work_date, minutes, reason }) => {
    if (!Number.isInteger(minutes) || minutes < 0 || minutes > 1440) {
      setError(t("leaves:workSchedules.minutesValidation"));
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await saveDailyScheduleOverride(userId, { work_date, minutes, reason });
      setDialogState(null);
      setSuccess(t("leaves:workSchedules.overrideSaved"));
      await loadPreview();
    } catch (err) {
      setError(err?.message || t("leaves:workSchedules.overrideSaveError"));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteOverride = async (userId, overrideId) => {
    if (!window.confirm(t("leaves:workSchedules.deleteConfirm"))) return;

    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await deleteDailyScheduleOverride(userId, overrideId);
      setDialogState(null);
      setSuccess(t("leaves:workSchedules.overrideDeleted"));
      await loadPreview();
    } catch (err) {
      setError(err?.message || t("leaves:workSchedules.overrideDeleteError"));
    } finally {
      setSaving(false);
    }
  };


  const handleQuickPreset = async (user, day, dayData, minutes) => {
    const source = normalizeSource(dayData?.source);
    if (!isSourceEditable(source)) return;

    const userId = Number(user?.id);
    if (!userId || !day?.dateOnly) return;

    const cellKey = `${userId}:${day.dateOnly}`;
    setQuickSavingKey(cellKey);
    setError("");
    setSuccess("");
    try {
      await saveDailyScheduleOverride(userId, {
        work_date: day.dateOnly,
        minutes,
        reason: buildCopyReason("Quick edit", user?.title, day.dateOnly),
      });
      setSuccess(t("leaves:workSchedules.quickEditSaved"));
      await loadPreview();
    } catch (err) {
      setError(err?.message || t("leaves:workSchedules.quickEditSaveError"));
    } finally {
      setQuickSavingKey("");
    }
  };

  const openCopyDayDialog = (user, day, value) => {
    const source = normalizeSource(value?.source);
    if (!isSourceEditable(source)) return;
    setSuccess("");
    setError("");
    setCopyDayState({ user, day, value });
  };

  const handleApplyCopyDay = async ({ targetUserIds, work_date, minutes, reason }) => {
    if (!Number.isInteger(minutes) || minutes < 0 || minutes > 1440) {
      setError(t("leaves:workSchedules.minutesValidation"));
      return;
    }

    const ids = (targetUserIds || []).map(Number).filter(Boolean);
    if (!ids.length) {
      setError(t("leaves:workSchedules.targetEmployeesRequired"));
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");
    try {
      // Keep the operation intentionally explicit and sequential.  It avoids a
      // new backend API in Batch 5 and keeps each one-day override persisted by
      // the same route that the normal cell dialog uses.  The expected target
      // set is small in reception work, and the predictable DB load is safer
      // than blasting many concurrent writes from the browser.
      for (const userId of ids) {
        await saveDailyScheduleOverride(userId, { work_date, minutes, reason });
      }
      setCopyDayState(null);
      setSuccess(t("leaves:workSchedules.copyDayApplied", { count: ids.length }));
      await loadPreview();
    } catch (err) {
      setError(err?.message || t("leaves:workSchedules.copyDayApplyError"));
    } finally {
      setSaving(false);
    }
  };

  const handleApplyCopyWeek = async ({ sourceRow, targetUserIds, targetWeekStart, repeatWeeks, reason }) => {
    const ids = (targetUserIds || []).map(Number).filter(Boolean);
    const repeat = Math.max(1, Math.min(12, Math.round(Number(repeatWeeks || 1))));
    const targetStart = toDateOnly(startOfWeekMonday(targetWeekStart));

    if (!ids.length) {
      setError(t("leaves:workSchedules.targetEmployeesRequired"));
      return;
    }
    if (!targetStart) {
      setError(t("leaves:workSchedules.targetWeekRequired"));
      return;
    }

    const sourceDays = (sourceRow?.days || []).filter((day) => normalizeSource(day.source) !== "FACILITY_EXCEPTION");
    if (!sourceDays.length) {
      setError(t("leaves:workSchedules.copyWeekNoSourceDays"));
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");
    try {
      let writes = 0;
      for (const userId of ids) {
        for (let weekIndex = 0; weekIndex < repeat; weekIndex += 1) {
          for (const sourceDay of sourceDays) {
            const sourceDate = parseDateOnly(sourceDay.work_date);
            const sourceWeek = parseDateOnly(weekStart);
            if (!sourceDate || !sourceWeek) continue;
            const dayOffset = Math.round((sourceDate.getTime() - sourceWeek.getTime()) / (24 * 60 * 60 * 1000));
            const workDate = shiftDateOnly(targetStart, (weekIndex * 7) + dayOffset);
            await saveDailyScheduleOverride(userId, {
              work_date: workDate,
              minutes: Number(sourceDay.minutes || 0),
              reason,
            });
            writes += 1;
          }
        }
      }
      setCopyWeekState(null);
      setSuccess(t("leaves:workSchedules.copyWeekApplied", { count: writes }));
      await loadPreview();
    } catch (err) {
      setError(err?.message || t("leaves:workSchedules.copyWeekApplyError"));
    } finally {
      setSaving(false);
    }
  };

  const handleSavePatternAssignment = async (userId, payload) => {
    if (!payload.template_id || !payload.effective_from || !payload.cycle_start_date) {
      setError(t("leaves:workSchedules.patternValidation"));
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await savePatternAssignment(userId, payload);
      setSuccess(t("leaves:workSchedules.patternAssignmentSaved"));
      await refreshPatternDialogAssignments({ id: userId });
      await loadPreview();
    } catch (err) {
      setError(err?.message || t("leaves:workSchedules.patternAssignmentSaveError"));
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePatternAssignment = async (userId, assignmentId) => {
    if (!window.confirm(t("leaves:workSchedules.clearPatternConfirm"))) return;

    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await deletePatternAssignment(userId, assignmentId);
      setSuccess(t("leaves:workSchedules.patternAssignmentCleared"));
      await refreshPatternDialogAssignments({ id: userId });
      await loadPreview();
    } catch (err) {
      setError(err?.message || t("leaves:workSchedules.patternAssignmentClearError"));
    } finally {
      setSaving(false);
    }
  };


  const openExactScheduleDialog = async (row, options = {}) => {
    const user = row?.user || {};
    setSuccess("");
    setError("");
    setExactLoading(true);

    const baseState = {
      user,
      mode: options.mode || "single",
      defaultEffectiveFrom: options.effectiveFrom || weekStart,
      defaultEffectiveTo: options.effectiveTo || weekEnd,
      days: options.days || buildDaysFromPreviewRow(row),
      schedule: null,
      warnings: [],
    };
    setExactDialogState(baseState);

    try {
      const schedules = await fetchExactSchedules(user.id);
      const activeForWeek = schedules.find((schedule) => (
        schedule.effective_from <= weekEnd && (!schedule.effective_to || schedule.effective_to >= weekStart)
      ));

      setExactDialogState({
        ...baseState,
        schedule: activeForWeek || null,
        effective_from: activeForWeek?.effective_from || baseState.defaultEffectiveFrom,
        effective_to: activeForWeek?.effective_to || baseState.defaultEffectiveTo,
        days: activeForWeek ? buildDaysFromExactSchedule(activeForWeek) : baseState.days,
      });
    } catch (err) {
      setError(err?.message || t("leaves:workSchedules.exactSchedulesLoadError"));
    } finally {
      setExactLoading(false);
    }
  };

  const openExactFromVisibleWeekDialog = (row) => {
    // Batch 3 workflow: convert the currently visible effective week into a
    // period-based exact weekly schedule for the selected employee.
    openExactScheduleDialog(row, {
      days: buildDaysFromPreviewRow(row),
      effectiveFrom: weekStart,
      effectiveTo: weekEnd,
    });
  };

  const openCopyWeekDialog = (row) => {
    // Batch 5 workflow: copy the visible effective week as one-day overrides to
    // another week and/or other employees.  This is intentionally separate from
    // exact schedules.  Reception uses it for fast operational roster copying
    // without changing the employee's base schedule policy.
    setSuccess("");
    setError("");
    setCopyWeekState({ row });
  };

  const openBulkExactScheduleDialog = () => {
    setSuccess("");
    setError("");
    setExactDialogState({
      user: { id: null, title: t("leaves:workSchedules.bulkApply") },
      mode: "bulk",
      defaultEffectiveFrom: weekStart,
      defaultEffectiveTo: weekEnd,
      days: buildEmptyWeekDays().map((day) => ({ ...day, minutes: day.day_of_week <= 5 ? 480 : 0 })),
      schedule: null,
      warnings: [],
    });
  };

  const handleSaveExactSchedule = async (userId, payload) => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const result = await saveExactSchedule(userId, payload);
      const warnings = Array.isArray(result?.warnings) ? result.warnings : [];
      setSuccess(warnings.length
        ? t("leaves:workSchedules.exactScheduleSavedWithWarnings")
        : t("leaves:workSchedules.exactScheduleSaved"));
      setExactDialogState(null);
      await loadPreview();
    } catch (err) {
      setError(err?.message || t("leaves:workSchedules.exactScheduleSaveError"));
    } finally {
      setSaving(false);
    }
  };

  const handleBulkSaveExactSchedule = async (payload) => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const result = await bulkSaveExactSchedule(payload);
      const warnings = Array.isArray(result?.warnings) ? result.warnings : [];
      setSuccess(warnings.length
        ? t("leaves:workSchedules.bulkExactScheduleSavedWithWarnings")
        : t("leaves:workSchedules.bulkExactScheduleSaved"));
      setExactDialogState(null);
      await loadPreview();
    } catch (err) {
      setError(err?.message || t("leaves:workSchedules.bulkExactScheduleSaveError"));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteExactSchedule = async (userId, effectiveFrom) => {
    if (!window.confirm(t("leaves:workSchedules.deleteExactScheduleConfirm"))) return;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await deleteExactSchedule(userId, effectiveFrom);
      setSuccess(t("leaves:workSchedules.exactScheduleDeleted"));
      setExactDialogState(null);
      await loadPreview();
    } catch (err) {
      setError(err?.message || t("leaves:workSchedules.exactScheduleDeleteError"));
    } finally {
      setSaving(false);
    }
  };


  const resetSavedView = () => {
    clearSavedWorkScheduleView();
    setWeekStart(initialWeekStart);
    setShowOnlyExceptions(false);
    setQuickEditMode(false);
    setSelectedUserIds(employees.map((row) => row.id));
    setSuccess(t("leaves:workSchedules.savedViewReset"));
  };

  const openAuditDialog = async (row) => {
    const user = row?.user || {};
    if (!user.id) return;

    setSuccess("");
    setError("");
    setAuditLoading(true);
    setAuditDialogState({ user, from: weekStart, to: weekEnd, entries: [] });
    try {
      const entries = await fetchWorkScheduleAudit(user.id, { from: weekStart, to: weekEnd });
      setAuditDialogState({ user, from: weekStart, to: weekEnd, entries });
    } catch (err) {
      setError(err?.message || t("leaves:workSchedules.audit.loadError"));
    } finally {
      setAuditLoading(false);
    }
  };

  const exportSchedulePdf = () => {
    // Owner-facing PDF export: intentionally generated client-side from the
    // already loaded preview so it cannot diverge from what the user sees in
    // the grid.  This is a printable roster/report, not a payroll document.
    const doc = new jsPDF("landscape", "mm", "a4");
    const title = t("leaves:workSchedules.ownerPdfTitle");
    const subtitle = `${weekStart} – ${weekEnd}`;

    doc.setFontSize(16);
    doc.text(title, 14, 14);
    doc.setFontSize(10);
    doc.text(subtitle, 14, 21);
    doc.text(t("leaves:workSchedules.ownerPdfGeneratedAt", { value: formatDateTime(new Date()) }), 14, 27);

    const head = [[
      t("leaves:workSchedules.employee"),
      ...weekDays.map((day) => `${t(`leaves:workSchedules.weekdays.${day.jsDay}`, { defaultValue: day.dateOnly })}\n${day.dateOnly}`),
      t("leaves:workSchedules.total"),
    ]];

    const body = visibleRows.map((row) => {
      const user = row.user || {};
      return [
        user.title || `User ${user.id}`,
        ...weekDays.map((day) => {
          const value = byUserAndDate.get(`${Number(user.id)}:${day.dateOnly}`);
          const flags = [];
          if (hasAbsenceInfo(value)) flags.push(t("leaves:workSchedules.printFlags.absence"));
          if (hasOperationalWarnings(value)) flags.push(t("leaves:workSchedules.printFlags.warning"));
          if (hasExceptionOrConflictDay(value)) flags.push(t("leaves:workSchedules.printFlags.exception"));
          return [formatMinutes(value?.minutes || 0, t), sourceLabel(value?.source, t), flags.join(" ")].filter(Boolean).join("\n");
        }),
        formatMinutes(row.totalMinutes || 0, t),
      ];
    });

    autoTable(doc, {
      head,
      body,
      startY: 34,
      styles: { fontSize: 7, cellPadding: 1.8, valign: "top" },
      headStyles: { fontStyle: "bold" },
      columnStyles: { 0: { cellWidth: 42 } },
      didDrawPage: () => {
        const pageCount = doc.internal.getNumberOfPages();
        doc.setFontSize(8);
        doc.text(`${t("common:page", { defaultValue: "Page" })} ${doc.internal.getCurrentPageInfo().pageNumber} / ${pageCount}`, 270, 200);
      },
    });

    if (Number(warningSummary.total || 0) > 0) {
      doc.addPage("landscape");
      doc.setFontSize(14);
      doc.text(t("leaves:workSchedules.warningSummaryTitle"), 14, 14);
      autoTable(doc, {
        head: [[t("leaves:workSchedules.warnings"), t("leaves:workSchedules.total")]],
        body: warningTypes.map((item) => [warningLabel({ type: item.type }, t), String(item.count)]),
        startY: 22,
        styles: { fontSize: 9 },
      });
    }

    doc.save(`work-schedule-roster-${weekStart}-${weekEnd}.pdf`);
  };

  const exportScheduleCsv = () => {
    const header = [
      t("leaves:workSchedules.employee"),
      ...weekDays.map((day) => day.dateOnly),
      t("leaves:workSchedules.total"),
    ];
    const lines = [header];
    for (const row of visibleRows) {
      const user = row.user || {};
      lines.push([
        user.title || `User ${user.id}`,
        ...weekDays.map((day) => {
          const value = byUserAndDate.get(`${Number(user.id)}:${day.dateOnly}`);
          return formatMinutes(value?.minutes || 0, t);
        }),
        formatMinutes(row.totalMinutes || 0, t),
      ]);
    }

    const csv = lines.map((line) => line.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `work-schedules-${weekStart}-${weekEnd}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const printSchedule = () => {
    window.print();
  };

  return (
    <main className="hrws-page">
      <header className="hrws-header">
        <div>
          <h1>{t("leaves:workSchedules.title")}</h1>
          <p>{t("leaves:workSchedules.subtitle")}</p>
        </div>
        <div className="hrws-header__actions">
          <button type="button" className="hrws-secondary-button" onClick={openBulkExactScheduleDialog} disabled={loading || saving}>
            {t("leaves:workSchedules.bulkApply")}
          </button>
          <button type="button" className="hrws-secondary-button" onClick={exportScheduleCsv} disabled={loading || visibleRows.length === 0}>
            {t("leaves:workSchedules.exportCsv")}
          </button>
          <button type="button" className="hrws-secondary-button" onClick={exportSchedulePdf} disabled={loading || visibleRows.length === 0}>
            {t("leaves:workSchedules.exportPdf")}
          </button>
          <button type="button" className="hrws-secondary-button" onClick={printSchedule} disabled={loading || visibleRows.length === 0}>
            {t("leaves:workSchedules.printRoster")}
          </button>
          <button type="button" className="hrws-secondary-button" onClick={resetSavedView} disabled={loading || saving}>
            {t("leaves:workSchedules.resetSavedView")}
          </button>
          <button type="button" className="hrws-secondary-button" onClick={() => loadPreview()} disabled={loading}>
            {loading ? t("common:loading", { defaultValue: "Loading..." }) : t("common:refresh", { defaultValue: "Refresh" })}
          </button>
        </div>
      </header>

      <section className="hrws-toolbar" aria-label={t("leaves:workSchedules.filters")}> 
        <div className="hrws-week-controls">
          <button type="button" className="hrws-secondary-button" onClick={() => goWeek(-1)}>
            ← {t("common:previous", { defaultValue: "Previous" })}
          </button>
          <label className="hrws-date-field">
            <span>{t("leaves:workSchedules.weekStart")}</span>
            <input
              type="date"
              value={weekStart}
              onChange={(event) => setWeekStart(toDateOnly(startOfWeekMonday(event.target.value)))}
            />
          </label>
          <button type="button" className="hrws-secondary-button" onClick={goToday}>
            {t("common:today", { defaultValue: "Today" })}
          </button>
          <button type="button" className="hrws-secondary-button" onClick={() => goWeek(1)}>
            {t("common:next", { defaultValue: "Next" })} →
          </button>
        </div>

        <div className="hrws-toolbar__employees">
          <h2>{t("leaves:workSchedules.employees")}</h2>
          <EmployeeMultiSelect
            employees={employees}
            selectedIds={selectedUserIds}
            onChange={setSelectedUserIds}
            labels={employeeLabels}
          />
        </div>
      </section>

      {(error || success) && (
        <section className="hrws-messages" aria-live="polite">
          {error && <div className="hrws-alert hrws-alert--error">{error}</div>}
          {success && <div className="hrws-alert hrws-alert--success">{success}</div>}
        </section>
      )}

      <section className="hrws-legend" aria-label={t("leaves:workSchedules.legend")}> 
        {["FACILITY_EXCEPTION", "EMPLOYEE_OVERRIDE", "USER_WORK_SCHEDULE", "WORK_PATTERN", "DEFAULT_DAILY_MINUTES", "NON_WORKING_DEFAULT"].map((source) => (
          <SourceBadge key={source} source={source} t={t} />
        ))}
      </section>

      <section className="hrws-productivity-bar" aria-label={t("leaves:workSchedules.productivityTools")}>
        <label className="hrws-toggle">
          <input
            type="checkbox"
            checked={showOnlyExceptions}
            onChange={(event) => setShowOnlyExceptions(event.target.checked)}
          />
          <span>{t("leaves:workSchedules.showOnlyExceptions")}</span>
        </label>
        <label className="hrws-toggle hrws-toggle--quick-edit">
          <input
            type="checkbox"
            checked={quickEditMode}
            onChange={(event) => setQuickEditMode(event.target.checked)}
          />
          <span>{t("leaves:workSchedules.quickEditMode")}</span>
        </label>
        <div className="hrws-productivity-stats">
          <span>{t("leaves:workSchedules.visibleEmployees", { count: visibleRows.length })}</span>
          <span>{t("leaves:workSchedules.exceptionCells", { count: visibleExceptionCellCount })}</span>
          <span>{t("leaves:workSchedules.visibleWeekTotal", { total: formatMinutes(visibleWeekTotalMinutes, t) })}</span>
        </div>
      </section>

      {Number(warningSummary.total || 0) > 0 && (
        <section className="hrws-warning-summary" aria-label={t("leaves:workSchedules.warningSummaryTitle")}> 
          <div>
            <strong>{t("leaves:workSchedules.warningSummaryTitle")}</strong>
            <p>{t("leaves:workSchedules.warningSummaryHelp")}</p>
          </div>
          <div className="hrws-warning-summary__chips">
            {warningTypes.map((item) => (
              <span key={item.type} className="hrws-warning-summary__chip">
                {warningLabel({ type: item.type }, t)}: <strong>{item.count}</strong>
              </span>
            ))}
          </div>
        </section>
      )}

      <section className="hrws-grid-card">
        <div className="hrws-grid-scroll">
          <table className="hrws-grid-table">
            <thead>
              <tr>
                <th className="hrws-employee-col">{t("leaves:workSchedules.employee")}</th>
                {weekDays.map((day) => (
                  <th key={day.dateOnly} className={day.isToday ? "hrws-today-header" : ""}>
                    <span>{t(`leaves:workSchedules.weekdays.${day.jsDay}`, { defaultValue: day.date.toLocaleDateString(undefined, { weekday: "short" }) })}</span>
                    <strong>{day.dayOfMonth}</strong>
                  </th>
                ))}
                <th className="hrws-total-col">{t("leaves:workSchedules.total")}</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => {
                const user = row.user || {};
                const rowHasException = rowHasExceptionOrConflict(row);
                return (
                  <tr key={user.id} className={rowHasException ? "hrws-row--has-exception" : ""}>
                    <th className="hrws-employee-col">
                      <strong>{user.title}</strong>
                      <span>{user.role}</span>
                      <WorkPatternSummary
                        assignment={row.workPatternAssignment}
                        onManage={() => openPatternDialog(row)}
                        t={t}
                      />
                      <div className="hrws-employee-actions">
                        <button type="button" className="hrws-link-button" onClick={() => openExactScheduleDialog(row)}>
                          {t("leaves:workSchedules.manageExactSchedule")}
                        </button>
                        <button type="button" className="hrws-link-button" onClick={() => openExactFromVisibleWeekDialog(row)}>
                          {t("leaves:workSchedules.copyVisibleWeek")}
                        </button>
                        <button type="button" className="hrws-link-button" onClick={() => openCopyWeekDialog(row)}>
                          {t("leaves:workSchedules.copyRepeatWeek")}
                        </button>
                        <button type="button" className="hrws-link-button" onClick={() => openAuditDialog(row)}>
                          {t("leaves:workSchedules.audit.open")}
                        </button>
                      </div>
                    </th>
                    {weekDays.map((day) => {
                      const value = byUserAndDate.get(`${Number(user.id)}:${day.dateOnly}`) || {
                        work_date: day.dateOnly,
                        minutes: 0,
                        source: "NON_WORKING_DEFAULT",
                      };
                      return (
                        <td key={day.dateOnly} className={day.isWeekend ? "hrws-weekend-col" : ""}>
                          <DayCell
                            user={user}
                            day={day}
                            value={value}
                            onOpen={openDialog}
                            onPreset={handleQuickPreset}
                            onCopyDay={openCopyDayDialog}
                            quickEditMode={quickEditMode}
                            quickSavingKey={quickSavingKey}
                            t={t}
                            dimmed={showOnlyExceptions && !hasExceptionOrConflictDay(value)}
                          />
                        </td>
                      );
                    })}
                    <td className="hrws-total-col">
                      <strong>{formatMinutes(row.totalMinutes || 0, t)}</strong>
                      <span>{t("leaves:workSchedules.workingDays", { count: row.workingDays || 0 })}</span>
                    </td>
                  </tr>
                );
              })}

              {!loading && visibleRows.length === 0 && (
                <tr>
                  <td className="hrws-empty" colSpan={weekDays.length + 2}>
                    {showOnlyExceptions
                      ? t("leaves:workSchedules.noExceptionRows")
                      : t("leaves:workSchedules.noRows")}
                  </td>
                </tr>
              )}
            </tbody>
            {visibleRows.length > 0 && (
              <tfoot>
                <tr>
                  <th className="hrws-employee-col hrws-day-total-label">
                    {t("leaves:workSchedules.dayTotals")}
                  </th>
                  {dayTotals.map((total) => (
                    <td key={total.dateOnly} className="hrws-day-total-cell">
                      <strong>{formatMinutes(total.minutes, t)}</strong>
                      <span>{t("leaves:workSchedules.peopleWorking", { count: total.people })}</span>
                      {total.exceptions > 0 && (
                        <em>{t("leaves:workSchedules.exceptionsShort", { count: total.exceptions })}</em>
                      )}
                    </td>
                  ))}
                  <td className="hrws-total-col hrws-day-total-cell">
                    <strong>{formatMinutes(visibleWeekTotalMinutes, t)}</strong>
                    <span>{t("leaves:workSchedules.visibleWeek")}</span>
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {loading && (
          <div className="hrws-loading-overlay">
            {t("common:loading", { defaultValue: "Loading..." })}
          </div>
        )}
      </section>

      <CopyDayDialog
        state={copyDayState}
        employees={employees}
        selectedUserIds={selectedUserIds}
        onClose={() => setCopyDayState(null)}
        onApply={handleApplyCopyDay}
        saving={saving}
        t={t}
      />

      <CopyWeekDialog
        state={copyWeekState}
        employees={employees}
        selectedUserIds={selectedUserIds}
        weekStart={weekStart}
        onClose={() => setCopyWeekState(null)}
        onApply={handleApplyCopyWeek}
        saving={saving}
        t={t}
      />

      <OverrideDialog
        state={dialogState}
        onClose={() => setDialogState(null)}
        onSave={handleSaveOverride}
        onDelete={handleDeleteOverride}
        saving={saving}
        t={t}
      />

      <PatternAssignmentDialog
        state={patternDialogState}
        workPatterns={workPatterns}
        onClose={() => setPatternDialogState(null)}
        onSave={handleSavePatternAssignment}
        onDelete={handleDeletePatternAssignment}
        saving={saving}
        loading={patternLoading}
        t={t}
      />

      <ExactScheduleDialog
        state={exactDialogState}
        employees={employees}
        selectedUserIds={selectedUserIds}
        onClose={() => setExactDialogState(null)}
        onSave={handleSaveExactSchedule}
        onBulkSave={handleBulkSaveExactSchedule}
        onDelete={handleDeleteExactSchedule}
        saving={saving}
        loading={exactLoading}
        t={t}
      />

      <AuditDialog
        state={auditDialogState}
        onClose={() => setAuditDialogState(null)}
        loading={auditLoading}
        t={t}
      />
    </main>
  );
};

export default WorkSchedulesPage;
